import parsePhoneNumber, { type CountryCode } from 'libphonenumber-js'

export interface PhoneDisplayOptions {
  countryCode?: string | null
  includeCountryCode?: boolean
}

const containsMergeField = (phone: string): boolean => {
  return /{{.*}}/.test(phone)
}

const normalizeCountryCode = (
  countryCode?: string | null
): CountryCode | undefined => {
  const normalizedCountryCode = countryCode?.trim().toUpperCase()
  return normalizedCountryCode ? (normalizedCountryCode as CountryCode) : undefined
}

const parsePhoneForDisplay = (phone: string, options?: PhoneDisplayOptions) => {
  const defaultCountry = normalizeCountryCode(options?.countryCode)

  try {
    return parsePhoneNumber(
      phone,
      defaultCountry ? { defaultCountry, extract: false } : { extract: false }
    )
  } catch {
    return undefined
  }
}

const formatTenDigitNationalNumber = (
  nationalNumber: string,
  countryCallingCode: string,
  includeCountryCode: boolean
): string => {
  const formattedNationalNumber = `(${nationalNumber.slice(
    0,
    3
  )}) ${nationalNumber.slice(3, 6)}-${nationalNumber.slice(6)}`

  return includeCountryCode
    ? `+${countryCallingCode} ${formattedNationalNumber}`
    : formattedNationalNumber
}

export function formatPhoneForDisplay(
  phone?: string | null,
  options?: PhoneDisplayOptions
): string {
  const phoneValue = String(phone || '').trim()
  if (!phoneValue || containsMergeField(phoneValue)) return phoneValue

  const parsedNumber = parsePhoneForDisplay(phoneValue, options)
  if (!parsedNumber) return phoneValue
  if (!parsedNumber.isValid()) return phoneValue

  const includeCountryCode = options?.includeCountryCode ?? true
  const { countryCallingCode, nationalNumber } = parsedNumber

  if (nationalNumber.length === 10 && countryCallingCode) {
    return formatTenDigitNationalNumber(
      nationalNumber,
      countryCallingCode,
      includeCountryCode
    )
  }

  return includeCountryCode
    ? parsedNumber.formatInternational()
    : parsedNumber.formatNational()
}
